import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Save, BarChart } from 'lucide-react';
import { toast } from 'sonner';

const FormWizard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    owner_name: '',
    business_name: '',
    cra_number: '',
    business_address: '',
    business_phone: '',
    cell_phone: '',
    accountant_name: '',
    accountant_address: '',
    accountant_phone: '',
    lawyer_name: '',
    lawyer_address: '',
    lawyer_phone: '',
    family_members: [{ name: '', relationship: '', age: '', position: '', fmv_individual1: 0, fmv_individual2: 0, fmv_joint: 0 }],
    personal_assets: {
      non_registered_investments: 0,
      stock_options: 0,
      rrsp: 0,
      sp_rrsp: 0,
      tfsa: 0,
      resp: 0,
      ipp_pension: 0,
      home_value: 0,
      real_estate: 0,
      other_non_liquid: 0,
      non_liquid_debt: 0,
      capital_gains_used: false,
      capital_gains_amount: 0
    },
    business_assets: {
      land_value: 0,
      livestock_value: 0,
      equipment_value: 0,
      fixed_asset_debt: 0,
      entity_type: '',
      opco_common_shares: 0,
      opco_pref_shares: 0,
      holdco_value: 0,
      company2_value: 0,
      company3_value: 0,
      current_business_value: 0,
      retained_earnings: 0,
      outstanding_loans: false,
      loan_balance: 0,
      lines_of_credit: false,
      loc_balance: 0,
      highest_loc_balance: 0,
      personal_guarantee: false,
      guarantee_balance: 0,
      small_business_deduction: false,
      bonusing_down: false
    },
    insurance_policies: [{ insured_name: '', policy_owner: '', sum_insured: 0, annual_cost: 0, purpose: '', date_purchased: '' }],
    employee_info: {
      full_time_count: 0,
      part_time_count: 0,
      benefits: [],
      key_people: [{ name: '', impact: '' }]
    },
    succession_plan: {
      spouse_income_death: false,
      spouse_income_disability: false,
      company_fate_death: '',
      has_will: false,
      will_last_reviewed: '',
      has_buy_sell: false,
      buy_sell_funded: false,
      buy_sell_reviewed: '',
      ownership_duration: '',
      retirement_plan: '',
      retirement_income_plan: '',
      core_values: '',
      business_vision: '',
      has_succession_plan: false,
      succession_story: '',
      estate_freeze: false,
      estate_freeze_date: '',
      estate_freeze_valuation: 0
    }
  });

  useEffect(() => {
    if (id) {
      loadPlan();
    }
  }, [id]);

  const loadPlan = async () => {
    try {
      const response = await axios.get(`${API}/farm-plans/${id}`);
      setFormData(response.data);
      toast.success('Plan loaded successfully');
    } catch (error) {
      toast.error('Failed to load plan');
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleArrayChange = (section, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (section, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], defaultItem]
    }));
  };

  const removeArrayItem = (section, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const savePlan = async () => {
    try {
      if (id) {
        await axios.put(`${API}/farm-plans/${id}`, formData);
        toast.success('Plan updated successfully');
      } else {
        const response = await axios.post(`${API}/farm-plans`, formData);
        toast.success('Plan created successfully');
        navigate(`/dashboard/${response.data.id}`);
      }
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const steps = [
    { title: 'Business Info', component: <BusinessInfoForm formData={formData} handleInputChange={handleInputChange} /> },
    { title: 'Professional Services', component: <ProfessionalServicesForm formData={formData} handleInputChange={handleInputChange} /> },
    { title: 'Family Information', component: <FamilyInfoForm formData={formData} handleArrayChange={handleArrayChange} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} /> },
    { title: 'Personal Assets', component: <PersonalAssetsForm formData={formData} handleInputChange={handleInputChange} /> },
    { title: 'Business Assets', component: <BusinessAssetsForm formData={formData} handleInputChange={handleInputChange} /> },
    { title: 'Insurance', component: <InsuranceForm formData={formData} handleArrayChange={handleArrayChange} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} /> },
    { title: 'Employees', component: <EmployeeForm formData={formData} handleInputChange={handleInputChange} handleArrayChange={handleArrayChange} addArrayItem={addArrayItem} removeArrayItem={removeArrayItem} /> },
    { title: 'Succession Planning', component: <SuccessionForm formData={formData} handleInputChange={handleInputChange} /> }
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F8F4] via-[#EEE8DC] to-[#F9F8F4] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#2E4F3E] mb-2">
            {id ? 'Edit' : 'Create'} Farm Succession Plan
          </h1>
          <p className="text-[#5A6B5E]">Step {currentStep + 1} of {steps.length}: {steps[currentStep].title}</p>
          <Progress value={progress} className="mt-4 h-2" data-testid="form-progress" />
        </div>

        <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="form-card">
          <CardHeader>
            <CardTitle className="text-2xl text-[#2E4F3E]">{steps[currentStep].title}</CardTitle>
          </CardHeader>
          <CardContent>
            {steps[currentStep].component}
          </CardContent>
        </Card>

        <div className="flex justify-between mt-8">
          <Button
            onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
            disabled={currentStep === 0}
            variant="outline"
            className="border-[#2E4F3E] text-[#2E4F3E] hover:bg-[#2E4F3E] hover:text-white"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Previous
          </Button>

          <div className="flex gap-3">
            <Button
              onClick={savePlan}
              variant="outline"
              className="border-[#C87941] text-[#C87941] hover:bg-[#C87941] hover:text-white"
              data-testid="save-plan-btn"
            >
              <Save className="mr-2 w-4 h-4" />
              Save
            </Button>

            {currentStep === steps.length - 1 ? (
              <Button
                onClick={async () => {
                  await savePlan();
                  if (formData.id || id) {
                    navigate(`/dashboard/${formData.id || id}`);
                  }
                }}
                className="bg-[#2E4F3E] hover:bg-[#3A6350] text-white"
                data-testid="view-dashboard-btn"
              >
                <BarChart className="mr-2 w-4 h-4" />
                View Dashboard
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentStep(prev => Math.min(steps.length - 1, prev + 1))}
                className="bg-[#C87941] hover:bg-[#B06835] text-white"
                data-testid="next-step-btn"
              >
                Next
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Form Components
const BusinessInfoForm = ({ formData, handleInputChange }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="owner_name">Owner Name *</Label>
        <Input id="owner_name" value={formData.owner_name} onChange={(e) => handleInputChange(null, 'owner_name', e.target.value)} data-testid="owner-name-input" />
      </div>
      <div>
        <Label htmlFor="business_name">Business Name *</Label>
        <Input id="business_name" value={formData.business_name} onChange={(e) => handleInputChange(null, 'business_name', e.target.value)} data-testid="business-name-input" />
      </div>
      <div>
        <Label htmlFor="cra_number">CRA Entity Number</Label>
        <Input id="cra_number" value={formData.cra_number} onChange={(e) => handleInputChange(null, 'cra_number', e.target.value)} data-testid="cra-number-input" />
      </div>
      <div>
        <Label htmlFor="business_phone">Business Phone</Label>
        <Input id="business_phone" value={formData.business_phone} onChange={(e) => handleInputChange(null, 'business_phone', e.target.value)} data-testid="business-phone-input" />
      </div>
      <div>
        <Label htmlFor="cell_phone">Cell Phone</Label>
        <Input id="cell_phone" value={formData.cell_phone} onChange={(e) => handleInputChange(null, 'cell_phone', e.target.value)} data-testid="cell-phone-input" />
      </div>
    </div>
    <div>
      <Label htmlFor="business_address">Business Address</Label>
      <Textarea id="business_address" value={formData.business_address} onChange={(e) => handleInputChange(null, 'business_address', e.target.value)} data-testid="business-address-input" />
    </div>
  </div>
);

const ProfessionalServicesForm = ({ formData, handleInputChange }) => (
  <div className="space-y-6">
    <div className="p-4 bg-[#2E4F3E]/5 rounded-lg">
      <h3 className="font-semibold text-[#2E4F3E] mb-4">Accountant Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="accountant_name">Name</Label>
          <Input id="accountant_name" value={formData.accountant_name} onChange={(e) => handleInputChange(null, 'accountant_name', e.target.value)} data-testid="accountant-name-input" />
        </div>
        <div>
          <Label htmlFor="accountant_phone">Phone</Label>
          <Input id="accountant_phone" value={formData.accountant_phone} onChange={(e) => handleInputChange(null, 'accountant_phone', e.target.value)} data-testid="accountant-phone-input" />
        </div>
      </div>
      <div className="mt-4">
        <Label htmlFor="accountant_address">Address</Label>
        <Textarea id="accountant_address" value={formData.accountant_address} onChange={(e) => handleInputChange(null, 'accountant_address', e.target.value)} data-testid="accountant-address-input" />
      </div>
    </div>
    <div className="p-4 bg-[#C87941]/5 rounded-lg">
      <h3 className="font-semibold text-[#2E4F3E] mb-4">Lawyer Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="lawyer_name">Name</Label>
          <Input id="lawyer_name" value={formData.lawyer_name} onChange={(e) => handleInputChange(null, 'lawyer_name', e.target.value)} data-testid="lawyer-name-input" />
        </div>
        <div>
          <Label htmlFor="lawyer_phone">Phone</Label>
          <Input id="lawyer_phone" value={formData.lawyer_phone} onChange={(e) => handleInputChange(null, 'lawyer_phone', e.target.value)} data-testid="lawyer-phone-input" />
        </div>
      </div>
      <div className="mt-4">
        <Label htmlFor="lawyer_address">Address</Label>
        <Textarea id="lawyer_address" value={formData.lawyer_address} onChange={(e) => handleInputChange(null, 'lawyer_address', e.target.value)} data-testid="lawyer-address-input" />
      </div>
    </div>
  </div>
);

const FamilyInfoForm = ({ formData, handleArrayChange, addArrayItem, removeArrayItem }) => (
  <div className="space-y-4">
    {formData.family_members.map((member, index) => (
      <div key={index} className="p-4 border-2 border-[#2E4F3E]/20 rounded-lg bg-[#F9F8F4]">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold text-[#2E4F3E]">Family Member {index + 1}</h4>
          {formData.family_members.length > 1 && (
            <Button variant="destructive" size="sm" onClick={() => removeArrayItem('family_members', index)} data-testid={`remove-family-member-${index}`}>Remove</Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={member.name} onChange={(e) => handleArrayChange('family_members', index, 'name', e.target.value)} data-testid={`family-name-${index}`} />
          </div>
          <div>
            <Label>Relationship</Label>
            <Input value={member.relationship} onChange={(e) => handleArrayChange('family_members', index, 'relationship', e.target.value)} data-testid={`family-relationship-${index}`} />
          </div>
          <div>
            <Label>Age</Label>
            <Input type="number" value={member.age} onChange={(e) => handleArrayChange('family_members', index, 'age', parseInt(e.target.value) || '')} data-testid={`family-age-${index}`} />
          </div>
          <div>
            <Label>Position in Business</Label>
            <Input value={member.position} onChange={(e) => handleArrayChange('family_members', index, 'position', e.target.value)} data-testid={`family-position-${index}`} />
          </div>
        </div>
      </div>
    ))}
    <Button onClick={() => addArrayItem('family_members', { name: '', relationship: '', age: '', position: '', fmv_individual1: 0, fmv_individual2: 0, fmv_joint: 0 })} variant="outline" className="w-full border-[#2E4F3E] text-[#2E4F3E]" data-testid="add-family-member-btn">
      Add Family Member
    </Button>
  </div>
);

const PersonalAssetsForm = ({ formData, handleInputChange }) => (
  <div className="space-y-6">
    <div className="p-4 bg-[#2E4F3E]/5 rounded-lg">
      <h3 className="font-semibold text-[#2E4F3E] mb-4">Liquid Assets</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Non-Registered Investments</Label>
          <Input type="number" value={formData.personal_assets.non_registered_investments} onChange={(e) => handleInputChange('personal_assets', 'non_registered_investments', parseFloat(e.target.value) || 0)} data-testid="non-registered-investments-input" />
        </div>
        <div>
          <Label>Stock Options</Label>
          <Input type="number" value={formData.personal_assets.stock_options} onChange={(e) => handleInputChange('personal_assets', 'stock_options', parseFloat(e.target.value) || 0)} data-testid="stock-options-input" />
        </div>
        <div>
          <Label>RRSP</Label>
          <Input type="number" value={formData.personal_assets.rrsp} onChange={(e) => handleInputChange('personal_assets', 'rrsp', parseFloat(e.target.value) || 0)} data-testid="rrsp-input" />
        </div>
        <div>
          <Label>SP-RRSP</Label>
          <Input type="number" value={formData.personal_assets.sp_rrsp} onChange={(e) => handleInputChange('personal_assets', 'sp_rrsp', parseFloat(e.target.value) || 0)} data-testid="sp-rrsp-input" />
        </div>
        <div>
          <Label>TFSA</Label>
          <Input type="number" value={formData.personal_assets.tfsa} onChange={(e) => handleInputChange('personal_assets', 'tfsa', parseFloat(e.target.value) || 0)} data-testid="tfsa-input" />
        </div>
        <div>
          <Label>RESP</Label>
          <Input type="number" value={formData.personal_assets.resp} onChange={(e) => handleInputChange('personal_assets', 'resp', parseFloat(e.target.value) || 0)} data-testid="resp-input" />
        </div>
        <div>
          <Label>IPP/Pension</Label>
          <Input type="number" value={formData.personal_assets.ipp_pension} onChange={(e) => handleInputChange('personal_assets', 'ipp_pension', parseFloat(e.target.value) || 0)} data-testid="ipp-pension-input" />
        </div>
      </div>
    </div>
    <div className="p-4 bg-[#C87941]/5 rounded-lg">
      <h3 className="font-semibold text-[#2E4F3E] mb-4">Non-Liquid Assets</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Home Value</Label>
          <Input type="number" value={formData.personal_assets.home_value} onChange={(e) => handleInputChange('personal_assets', 'home_value', parseFloat(e.target.value) || 0)} data-testid="home-value-input" />
        </div>
        <div>
          <Label>Real Estate</Label>
          <Input type="number" value={formData.personal_assets.real_estate} onChange={(e) => handleInputChange('personal_assets', 'real_estate', parseFloat(e.target.value) || 0)} data-testid="real-estate-input" />
        </div>
        <div>
          <Label>Other Non-Liquid Assets</Label>
          <Input type="number" value={formData.personal_assets.other_non_liquid} onChange={(e) => handleInputChange('personal_assets', 'other_non_liquid', parseFloat(e.target.value) || 0)} data-testid="other-non-liquid-input" />
        </div>
        <div>
          <Label>Non-Liquid Asset Debt</Label>
          <Input type="number" value={formData.personal_assets.non_liquid_debt} onChange={(e) => handleInputChange('personal_assets', 'non_liquid_debt', parseFloat(e.target.value) || 0)} data-testid="non-liquid-debt-input" />
        </div>
      </div>
    </div>
  </div>
);

const BusinessAssetsForm = ({ formData, handleInputChange }) => (
  <div className="space-y-6">
    <div className="p-4 bg-[#2E4F3E]/5 rounded-lg">
      <h3 className="font-semibold text-[#2E4F3E] mb-4">Fixed Assets</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Land Value</Label>
          <Input type="number" value={formData.business_assets.land_value} onChange={(e) => handleInputChange('business_assets', 'land_value', parseFloat(e.target.value) || 0)} data-testid="land-value-input" />
        </div>
        <div>
          <Label>Livestock Value</Label>
          <Input type="number" value={formData.business_assets.livestock_value} onChange={(e) => handleInputChange('business_assets', 'livestock_value', parseFloat(e.target.value) || 0)} data-testid="livestock-value-input" />
        </div>
        <div>
          <Label>Equipment Value</Label>
          <Input type="number" value={formData.business_assets.equipment_value} onChange={(e) => handleInputChange('business_assets', 'equipment_value', parseFloat(e.target.value) || 0)} data-testid="equipment-value-input" />
        </div>
        <div>
          <Label>Fixed Asset Debt</Label>
          <Input type="number" value={formData.business_assets.fixed_asset_debt} onChange={(e) => handleInputChange('business_assets', 'fixed_asset_debt', parseFloat(e.target.value) || 0)} data-testid="fixed-asset-debt-input" />
        </div>
      </div>
    </div>
    <div className="p-4 bg-[#C87941]/5 rounded-lg">
      <h3 className="font-semibold text-[#2E4F3E] mb-4">Business Entity</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Entity Type</Label>
          <Input value={formData.business_assets.entity_type} onChange={(e) => handleInputChange('business_assets', 'entity_type', e.target.value)} placeholder="Proprietorship, Partnership, Corporation" data-testid="entity-type-input" />
        </div>
        <div>
          <Label>Current Business Value</Label>
          <Input type="number" value={formData.business_assets.current_business_value} onChange={(e) => handleInputChange('business_assets', 'current_business_value', parseFloat(e.target.value) || 0)} data-testid="current-business-value-input" />
        </div>
        <div>
          <Label>OPCO Common Shares</Label>
          <Input type="number" value={formData.business_assets.opco_common_shares} onChange={(e) => handleInputChange('business_assets', 'opco_common_shares', parseFloat(e.target.value) || 0)} data-testid="opco-common-shares-input" />
        </div>
        <div>
          <Label>Holdco Value</Label>
          <Input type="number" value={formData.business_assets.holdco_value} onChange={(e) => handleInputChange('business_assets', 'holdco_value', parseFloat(e.target.value) || 0)} data-testid="holdco-value-input" />
        </div>
      </div>
    </div>
  </div>
);

const InsuranceForm = ({ formData, handleArrayChange, addArrayItem, removeArrayItem }) => (
  <div className="space-y-4">
    {formData.insurance_policies.map((policy, index) => (
      <div key={index} className="p-4 border-2 border-[#2E4F3E]/20 rounded-lg bg-[#F9F8F4]">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold text-[#2E4F3E]">Insurance Policy {index + 1}</h4>
          {formData.insurance_policies.length > 1 && (
            <Button variant="destructive" size="sm" onClick={() => removeArrayItem('insurance_policies', index)} data-testid={`remove-insurance-${index}`}>Remove</Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Insured Name</Label>
            <Input value={policy.insured_name} onChange={(e) => handleArrayChange('insurance_policies', index, 'insured_name', e.target.value)} data-testid={`insured-name-${index}`} />
          </div>
          <div>
            <Label>Policy Owner</Label>
            <Input value={policy.policy_owner} onChange={(e) => handleArrayChange('insurance_policies', index, 'policy_owner', e.target.value)} data-testid={`policy-owner-${index}`} />
          </div>
          <div>
            <Label>Sum Insured</Label>
            <Input type="number" value={policy.sum_insured} onChange={(e) => handleArrayChange('insurance_policies', index, 'sum_insured', parseFloat(e.target.value) || 0)} data-testid={`sum-insured-${index}`} />
          </div>
          <div>
            <Label>Annual Cost</Label>
            <Input type="number" value={policy.annual_cost} onChange={(e) => handleArrayChange('insurance_policies', index, 'annual_cost', parseFloat(e.target.value) || 0)} data-testid={`annual-cost-${index}`} />
          </div>
          <div>
            <Label>Purpose</Label>
            <Input value={policy.purpose} onChange={(e) => handleArrayChange('insurance_policies', index, 'purpose', e.target.value)} data-testid={`purpose-${index}`} />
          </div>
          <div>
            <Label>Date Purchased</Label>
            <Input type="date" value={policy.date_purchased} onChange={(e) => handleArrayChange('insurance_policies', index, 'date_purchased', e.target.value)} data-testid={`date-purchased-${index}`} />
          </div>
        </div>
      </div>
    ))}
    <Button onClick={() => addArrayItem('insurance_policies', { insured_name: '', policy_owner: '', sum_insured: 0, annual_cost: 0, purpose: '', date_purchased: '' })} variant="outline" className="w-full border-[#2E4F3E] text-[#2E4F3E]" data-testid="add-insurance-btn">
      Add Insurance Policy
    </Button>
  </div>
);

const EmployeeForm = ({ formData, handleInputChange, handleArrayChange, addArrayItem, removeArrayItem }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label>Full-time Employees</Label>
        <Input type="number" value={formData.employee_info.full_time_count} onChange={(e) => handleInputChange('employee_info', 'full_time_count', parseInt(e.target.value) || 0)} data-testid="full-time-count-input" />
      </div>
      <div>
        <Label>Part-time Employees</Label>
        <Input type="number" value={formData.employee_info.part_time_count} onChange={(e) => handleInputChange('employee_info', 'part_time_count', parseInt(e.target.value) || 0)} data-testid="part-time-count-input" />
      </div>
    </div>
    <div className="mt-6">
      <h4 className="font-semibold text-[#2E4F3E] mb-4">Key Personnel</h4>
      {formData.employee_info.key_people.map((person, index) => (
        <div key={index} className="p-4 border-2 border-[#2E4F3E]/20 rounded-lg bg-[#F9F8F4] mb-4">
          <div className="flex justify-between items-center mb-4">
            <h5 className="font-semibold text-[#2E4F3E]">Key Person {index + 1}</h5>
            {formData.employee_info.key_people.length > 1 && (
              <Button variant="destructive" size="sm" onClick={() => {
                const newKeyPeople = formData.employee_info.key_people.filter((_, i) => i !== index);
                handleInputChange('employee_info', 'key_people', newKeyPeople);
              }} data-testid={`remove-key-person-${index}`}>Remove</Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Name</Label>
              <Input value={person.name} onChange={(e) => {
                const newKeyPeople = [...formData.employee_info.key_people];
                newKeyPeople[index] = { ...newKeyPeople[index], name: e.target.value };
                handleInputChange('employee_info', 'key_people', newKeyPeople);
              }} data-testid={`key-person-name-${index}`} />
            </div>
            <div>
              <Label>Impact if Lost</Label>
              <Textarea value={person.impact} onChange={(e) => {
                const newKeyPeople = [...formData.employee_info.key_people];
                newKeyPeople[index] = { ...newKeyPeople[index], impact: e.target.value };
                handleInputChange('employee_info', 'key_people', newKeyPeople);
              }} data-testid={`key-person-impact-${index}`} />
            </div>
          </div>
        </div>
      ))}
      <Button onClick={() => {
        const newKeyPeople = [...formData.employee_info.key_people, { name: '', impact: '' }];
        handleInputChange('employee_info', 'key_people', newKeyPeople);
      }} variant="outline" className="w-full border-[#2E4F3E] text-[#2E4F3E]" data-testid="add-key-person-btn">
        Add Key Person
      </Button>
    </div>
  </div>
);

const SuccessionForm = ({ formData, handleInputChange }) => (
  <div className="space-y-6">
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Checkbox id="has_will" checked={formData.succession_plan.has_will} onCheckedChange={(checked) => handleInputChange('succession_plan', 'has_will', checked)} data-testid="has-will-checkbox" />
        <Label htmlFor="has_will" className="cursor-pointer">Do you have a will?</Label>
      </div>
      {formData.succession_plan.has_will && (
        <div>
          <Label>When was it last reviewed?</Label>
          <Input type="date" value={formData.succession_plan.will_last_reviewed} onChange={(e) => handleInputChange('succession_plan', 'will_last_reviewed', e.target.value)} data-testid="will-last-reviewed-input" />
        </div>
      )}
    </div>
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Checkbox id="has_buy_sell" checked={formData.succession_plan.has_buy_sell} onCheckedChange={(checked) => handleInputChange('succession_plan', 'has_buy_sell', checked)} data-testid="has-buy-sell-checkbox" />
        <Label htmlFor="has_buy_sell" className="cursor-pointer">Do you have a buy/sell agreement?</Label>
      </div>
    </div>
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Checkbox id="has_succession_plan" checked={formData.succession_plan.has_succession_plan} onCheckedChange={(checked) => handleInputChange('succession_plan', 'has_succession_plan', checked)} data-testid="has-succession-plan-checkbox" />
        <Label htmlFor="has_succession_plan" className="cursor-pointer">Do you have a succession plan?</Label>
      </div>
      {formData.succession_plan.has_succession_plan && (
        <div>
          <Label>Succession Story</Label>
          <Textarea value={formData.succession_plan.succession_story} onChange={(e) => handleInputChange('succession_plan', 'succession_story', e.target.value)} rows={4} data-testid="succession-story-input" />
        </div>
      )}
    </div>
    <div>
      <Label>How long do you intend to keep the business?</Label>
      <Input value={formData.succession_plan.ownership_duration} onChange={(e) => handleInputChange('succession_plan', 'ownership_duration', e.target.value)} placeholder="e.g., 5-10 years" data-testid="ownership-duration-input" />
    </div>
    <div>
      <Label>What will happen when you retire?</Label>
      <Input value={formData.succession_plan.retirement_plan} onChange={(e) => handleInputChange('succession_plan', 'retirement_plan', e.target.value)} placeholder="e.g., Sell to family, Sell to third party" data-testid="retirement-plan-input" />
    </div>
    <div>
      <Label>Business Core Values</Label>
      <Textarea value={formData.succession_plan.core_values} onChange={(e) => handleInputChange('succession_plan', 'core_values', e.target.value)} rows={3} data-testid="core-values-input" />
    </div>
    <div>
      <Label>Business Vision</Label>
      <Textarea value={formData.succession_plan.business_vision} onChange={(e) => handleInputChange('succession_plan', 'business_vision', e.target.value)} rows={3} data-testid="business-vision-input" />
    </div>
  </div>
);

export default FormWizard;